import { DocumentLoadEvent, PageChangeEvent, RenderPage, RenderPageProps, ZoomEvent } from '@react-pdf-viewer/core';
import { useQuery } from '@tanstack/react-query';
import { uniqueId } from 'lodash';
import { ChangeEvent, MouseEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RndDragCallback, RndResizeCallback } from 'react-rnd';
import { AnyObject } from 'yup';
import { getContractSealPdf } from '../../api/contract';
import { getDefaultSizeSealStamp, getSealPageData } from '../../api/sealStamp';
import TabsSign from '../../components/sign/TabsSign';
import HeaderSign from '../../components/sign/header/HeaderSign';
import HeaderSignAction from '../../components/sign/header/HeaderSignAction';
import CardBox from '../../components/ui/containers/CardBox';
import StickyBox from '../../components/ui/containers/StickyBox';
import SignProvider from '../../components/ui/signs/SignProvider';
import SignRenderPage from '../../components/ui/viewer/SignRenderPage';
import Viewer from '../../components/ui/viewer/Viewer';
import { queryKey } from '../../constants/queryKey';
import { useAlert } from '../../hooks/useAlert';
import { useCheckReStampPossible } from '../../hooks/useCheckReStampPossible';
import { pdfData } from '../../model/api/contract';
import { Sign } from '../../model/sign';
import SysConfigStore from '../../store/common/SysConfigStore';

interface DetailPageProps {
	contractId: string;
	documentId?: string | null;
}

/**
 * 인감 날인 페이지
 * 주의 : Axios로 가져온 pdfAxiosRes의 ArrayBuffer 메모리가 Viewer로 빠지면서,
 * Worker만 접근이 가능해, pdfObject로 따로 상태관리 해야 함
 */
export default function DetailPage({ contractId, documentId: propDocumentId }: DetailPageProps) {
	const { labelText, isComponentEnabled } = SysConfigStore();
	const { t } = useTranslation();
	const [snackbar] = useAlert();

	const documentId = useMemo(() => {
		// propDocumentId가 'null' 문자열이거나 빈 문자열인 경우 null로 처리
		return !propDocumentId || propDocumentId === 'null' || propDocumentId === '' ? null : propDocumentId;
	}, [propDocumentId]);

	// 재날인 가능한지 hook으로 파악하는 변수
	const isReStampPossible = useCheckReStampPossible(contractId, documentId);

	const [signs, setSigns] = useState<Sign[]>([]);
	const [doNotSigns, setDoNotSigns] = useState<Sign[]>([]);
	const [readOnlySign, setReadOnlySign] = useState<Sign[]>([]);
	const [zoom, setZoom] = useState(1);
	const [pageInfo, setPageInfo] = useState({
		width: 0,
		height: 0,
	});
	const [pdfObject, setPdfObject] = useState<pdfData | null>(null);
	const [goToPageIndex, setGoToPageIndex] = useState<number | null>(null);
	const [saveSize, setSaveSize] = useState(0);
	const [tabValue, setTabValue] = useState(0);

	useEffect(() => {
		// isComponentEnabled 조건을 안 걸어주면, documentId를 아직 documentId를 넘겨받지 못한 것인지, 아니면 원래 documentId가 필요 없는 것인지 구분이 되지 않는다.
		if (isComponentEnabled('CMPNT419_MULTI_DOCUMENT_AND_ATTACHMENT_SIGN_BF') && !documentId) {
			snackbar(
				t('cmmn_warning_cannot_found_document_for_sign_seal').replaceAll('{{날인}}', labelText('seal')),
				'error',
			);
			return;
		}

		// 재날인을 하는 상황
		if (isReStampPossible) {
			getSealPageData(contractId, documentId, isComponentEnabled)
				.then(res => {
					const stampDataList = res.data.stamp_data_list ?? [];
					const userDict = res.data.stamp_sealing_user_dict ?? {};

					const editableSigns: Sign[] = [];
					const readOnlySigns: Sign[] = [];

					stampDataList.forEach(sign => {
						if (userDict[sign.id]) {
							editableSigns.push(sign);
						} else {
							readOnlySigns.push(sign);
						}
					});

					setSigns(editableSigns);
					setDoNotSigns(readOnlySigns);
				})
				.catch(() => {
					snackbar('{{ MSGCons.trans().modify_seal_stamp_size_err_cannot_get_stamp_info }}', 'error');
				});
		} else {
			// 새로 날인을 하거나, 재날인 중 실수로 새로고침을 누른 상황(이 경우 재날인이 아닌 일반 날인으로 간주)
			getSealPageData(contractId, documentId, isComponentEnabled).then(res => {
				setReadOnlySign(res.data.stamp_data_list);
			});
		}
	}, [isReStampPossible, contractId, isComponentEnabled]);

	const { data: pdfAxiosRes, isLoading: pdfAxiosLoading } = useQuery(
		[queryKey.sealPdfData, contractId, isReStampPossible, documentId],
		() => getContractSealPdf(contractId, isReStampPossible ?? false, documentId ?? null),
		{
			enabled: isReStampPossible !== null,
			onError: error => {
				snackbar(t('contract_warn_no_seal_stamp').replace('{{날인}}', labelText('seal')), 'error');
				window.location.href = '/';
			},
		},
	);

	const getCurrentZoomFromDOM = (): number => {
		const zoomEl = document.querySelector('[data-testid="zoom__popover-target-scale"]');
		if (!zoomEl) return 1;

		const text = zoomEl.textContent?.replace('%', '');
		const scale = Number(text);
		return Number.isNaN(scale) ? 1 : scale / 100;
	};

	// 기존의 unique ID와 겹치지 않도록 신규 ID 생성
	const generateUUId = (baseId: string): string => {
		const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		const array = new Uint8Array(6);
		crypto.getRandomValues(array);

		const uuid = Array.from(array, byte => charset[byte % charset.length]).join('');
		return `${baseId}-${uuid}`;
	};

	const handleDragStop: RndDragCallback = useCallback(
		(e, data) => {
			if (!data) return;
			const currentZoom = getCurrentZoomFromDOM();
			const {
				x: prevX,
				y: prevY,
				node: {
					dataset: { id },
				},
			} = data;

			setSigns(prev =>
				prev.map(sign => {
					const { id: signId } = sign;

					const x = prevX / currentZoom;
					const y = prevY / currentZoom;
					return signId !== id ? sign : { ...sign, x, y };
				}),
			);
		},
		[signs],
	);

	// 날인 이미지 사이즈 조정
	const handleResizeStop: RndResizeCallback = useCallback(
		(e, dir, elementRef, delta, position) => {
			const currentZoom = getCurrentZoomFromDOM();
			if (!elementRef || !position) return;
			const {
				offsetWidth: width,
				offsetHeight: height,
				dataset: { id },
			} = elementRef;
			const { x, y } = position;

			setSigns(prev =>
				prev.map(sign => {
					const { id: signId } = sign;
					return signId !== id
						? sign
						: {
								...sign,
								width: width / currentZoom,
								height: height / currentZoom,
								x: x / currentZoom,
								y: y / currentZoom,
						  };
				}),
			);
		},
		[signs],
	);

	// 날인 리스트에서 계약서로 이미지 추가
	const handleDrop = useCallback(
		async (item: AnyObject, dropResult: AnyObject) => {
			const { id, imageUrl, name, width, height, x: cursorX, y: cursorY } = item;
			const { id: pageIndex, viewerZoom, x: pageCursorX, y: pageCursorY, pageWidth, pageHeight } = dropResult;
			const x = pageCursorX - cursorX;
			const y = pageCursorY - cursorY;

			setZoom(viewerZoom);

			setPageInfo({
				width: pageWidth / viewerZoom,
				height: pageHeight / viewerZoom,
			});

			try {
				const { width: defaultWidth, height: defaultHeight } = await getDefaultSizeSealStamp(
					id,
					width,
					height,
					isComponentEnabled,
				);

				const baseId = id.toString().split('-')[0];
				const newId = generateUUId(baseId);

				setSigns(prev => [
					...prev,
					{
						id: newId,
						name,
						pageIndex: Number(pageIndex),
						imageUrl,
						x: x / viewerZoom,
						y: y / viewerZoom,
						// width: width / viewerZoom,
						// height: height / viewerZoom,
						// width: (defaultWidth || width) / viewerZoom,
						// height: (defaultHeight || height) / viewerZoom,
						width: defaultWidth,
						height: defaultHeight,
					},
				]);
			} catch (error) {
				setSigns(prev => [
					...prev,
					{
						id: uniqueId(`${id}-`),
						name,
						pageIndex: Number(pageIndex),
						imageUrl,
						x: x / viewerZoom,
						y: y / viewerZoom,
						width: width / viewerZoom,
						height: height / viewerZoom,
					},
				]);
			}
		},
		[signs, zoom],
	);

	// PDF 확대 축소
	const handleZoom = useCallback(
		(e: ZoomEvent) => {
			const { scale = 1 } = e;
			setZoom(scale);
		},
		[signs, zoom],
	);

	// 날인 이미지 입력으로 사이즈 조정
	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>, sign: Sign) => {
			const {
				target: { value: v },
			} = e;
			const value = Number(v);
			const { id, width: imageWidth, height: imageHeight } = sign;

			const baseId = id.toString().split('-')[0];

			const newId = generateUUId(baseId);

			let pageWidth = pageInfo.width;
			if (pageWidth === 0) {
				const pageLayer = document.querySelector('.rpv-core__page-layer') as HTMLDivElement;
				if (pageLayer) {
					pageWidth = pageLayer.getBoundingClientRect().width;
				}
			}

			const minValue = value < 40 ? 40 : value;

			const width = minValue > pageWidth ? pageWidth : minValue;
			const height = (imageHeight * width) / imageWidth;

			setSigns(prev => {
				return [
					...prev.map(item =>
						item.id === id
							? ({
									...item,
									id: newId,
									width,
									height,
							  } as Sign)
							: item,
					),
				];
			});
		},
		[signs, zoom],
	);

	// 날인 이미지 삭제
	const handleDeleteSign = useCallback(
		(e: MouseEvent<HTMLButtonElement>, sign: Sign) => {
			const { id } = sign;
			setSigns(prev => prev.filter(({ id: signId }) => signId !== id));
		},
		[signs, zoom],
	);

	// 날인 이미지 사이즈 저장
	const handleSaveSize = useCallback(
		async (e: MouseEvent<HTMLButtonElement>, sign: Sign) => {
			const { width } = sign;

			setSaveSize(width);
		},
		[signs, zoom, isComponentEnabled],
	);

	// 날인 저장된 이미지 사이즈 불러오기
	const handleLoadSize = useCallback(
		(e: MouseEvent<HTMLButtonElement>, sign: Sign) => {
			const { id, width: imageWidth, height: imageHeight } = sign;

			const { width: pageWidth } = pageInfo;

			const baseId = id.toString().split('-')[0];
			const newId = generateUUId(baseId);

			const width = Number(saveSize) > pageWidth ? pageWidth : Number(saveSize);
			const height = (imageHeight * width) / imageWidth;

			setSigns(prev => {
				return [
					...prev.map(item =>
						item.id === id
							? ({
									...item,
									id: newId,
									width,
									height,
							  } as Sign)
							: item,
					),
				];
			});
		},
		[signs, zoom],
	);

	// PDF 페이지 렌더링
	const renderPage: RenderPage = useCallback(
		(props: RenderPageProps) => (
			<SignRenderPage
				signs={signs}
				noSigns={doNotSigns}
				onDragStop={handleDragStop}
				onResizeStop={handleResizeStop}
				onDeleteSign={handleDeleteSign}
				onChange={handleChange}
				onSaveSize={handleSaveSize}
				onLoadSize={handleLoadSize}
				enableResizing={!isComponentEnabled('CMPNT362_DISABLE_SEAL_RESIZE')}
				{...props}
			/>
		),
		[signs, zoom],
	);

	// PDF 페이지 load 후
	const handleDocumentLoad = async (e: DocumentLoadEvent) => {
		const fileArray = await e.doc.getData();
		const fileName = pdfAxiosRes?.fileName;

		if (fileName) setPdfObject({ file: fileArray, fileName });
	};

	// PDF 페이지 사용자가 변경한 경우
	const handlePageChange = useCallback((e: PageChangeEvent) => setGoToPageIndex(null), []);

	// PDF 페이지 강제 이동
	const handleGoToPage = useCallback((e: AnyObject, pageIndex: number) => setGoToPageIndex(pageIndex), [signs, zoom]);

	// 날인 탭 이동
	const handleChangeTab = useCallback((_: SyntheticEvent, v: number) => setTabValue(v), [signs, zoom]);

	return (
		<section className='flex flex-col gap-5 p-8 max-w-[1920px] mx-auto'>
			<HeaderSign title={pdfObject?.fileName || ''} contractId={contractId} />

			<div>
				<SignProvider>
					<div className='flex gap-5'>
						<CardBox className='basis-8/12 p-5'>
							{!pdfAxiosLoading && pdfAxiosRes && (
								<Viewer
									fileUrl={pdfAxiosRes.file}
									goToPageIndex={goToPageIndex}
									onPageChange={handlePageChange}
									renderPage={renderPage}
									onZoom={handleZoom}
									onDocumentLoad={handleDocumentLoad}
								/>
							)}
						</CardBox>
						<div className='basis-4/12'>
							<StickyBox className='grid gap-4'>
								{pdfObject && (
									<HeaderSignAction
										signs={signs}
										pdfObject={pdfObject}
										contractId={contractId}
										isReStampPossible={isReStampPossible ?? false}
										noSigns={doNotSigns}
										documentId={documentId}
									/>
								)}

								<TabsSign
									contractId={contractId}
									signs={signs}
									value={tabValue}
									onChange={handleChangeTab}
									onDrop={handleDrop}
									onDeleteSign={handleDeleteSign}
									onGoToPage={handleGoToPage}
									isReStampPossible={isReStampPossible ?? false}
								/>
							</StickyBox>
						</div>
					</div>
				</SignProvider>
			</div>
		</section>
	);
}
